import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MessageCircle, User, Award, ChevronRight } from 'lucide-react';
import api from '../services/api';

interface Scene {
  id: string;
  speaker: {
    name: string;
    title: string;
  };
  content: string;
  emotion: string;
  background: string;
  choices: Array<{
    text: string;
    nextScene: string;
    impact: string;
  }>;
  animation: {
    type: string;
    duration: number;
  };
}

interface Story {
  _id: string;
  title: string;
  chapter: string;
  order: number;
  scenes: Scene[];
  rewards: {
    badge: string;
    unlockContent: string;
  };
}

const MentorStoryPage: React.FC = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();

  const [story, setStory] = useState<Story | null>(null);
  const [currentScene, setCurrentScene] = useState<Scene | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [showChoices, setShowChoices] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    loadStory();
  }, [storyId]);

  const loadStory = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/mentor-story/${storyId}`);
      const storyData = response.data.data;
      setStory(storyData);
      setCurrentScene(storyData.scenes[0]);

      // 延迟显示选择按钮
      setTimeout(() => setShowChoices(true), 2000);
    } catch (error) {
      console.error('加载故事失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChoice = async (choiceIndex: number) => {
    if (!currentScene || !story) return;

    setSelectedChoice(choiceIndex);
    const choice = currentScene.choices[choiceIndex];

    try {
      const response = await api.post('/mentor-story/choice', {
        storyId: story._id,
        sceneId: currentScene.id,
        choiceIndex
      });

      const { nextScene } = response.data.data;

      if (nextScene.id === 'scene_end') {
        // 完成故事
        await completeStory();
      } else {
        // 切换到下一个场景
        setTimeout(() => {
          setCurrentScene(nextScene);
          setSelectedChoice(null);
          setShowChoices(false);
          setTimeout(() => setShowChoices(true), 1500);
        }, 1000);
      }
    } catch (error) {
      console.error('处理选择失败:', error);
    }
  };

  const completeStory = async () => {
    if (!story) return;

    try {
      await api.post('/mentor-story/complete', {
        storyId: story._id
      });
      setCompleted(true);
    } catch (error) {
      console.error('完成故事失败:', error);
    }
  };

  const getSpeakerAvatar = (speakerName: string) => {
    switch (speakerName) {
      case '陆鸣':
        return '👮‍♂️';
      case '老韩':
        return '👨‍🦳';
      default:
        return '🌟';
    }
  };

  const getEmotionColor = (emotion: string) => {
    switch (emotion) {
      case 'thoughtful':
        return 'text-blue-600';
      case 'calm':
        return 'text-gray-700';
      case 'approving':
        return 'text-green-600';
      case 'serious':
        return 'text-red-600';
      case 'warm':
        return 'text-orange-600';
      case 'wise':
        return 'text-purple-600';
      case 'inspiring':
        return 'text-yellow-600';
      case 'triumphant':
        return 'text-yellow-500';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-yellow-100 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center"
        >
          <Award className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">恭喜完成！</h2>
          <p className="text-gray-600 mb-4">你已获得「{story?.rewards.badge}」勋章</p>
          <p className="text-sm text-gray-500 mb-6">解锁内容：{story?.rewards.unlockContent}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回主页
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      {/* 顶部导航 */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            返回
          </button>
          <h1 className="text-xl font-bold">{story?.title}</h1>
          <div className="w-20"></div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {currentScene && (
            <motion.div
              key={currentScene.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              {/* 场景背景描述 */}
              <div className="text-center mb-6">
                <p className="text-gray-500 italic">{currentScene.background}</p>
              </div>

              {/* 对话内容 */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                <div className="flex items-start mb-4">
                  <div className="text-4xl mr-4">
                    {getSpeakerAvatar(currentScene.speaker.name)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="font-bold text-lg mr-2">{currentScene.speaker.name}</h3>
                      <span className="text-sm text-gray-500">{currentScene.speaker.title}</span>
                    </div>
                    <motion.div
                      className={`text-gray-800 leading-relaxed ${getEmotionColor(currentScene.emotion)}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 1 }}
                    >
                      {currentScene.content.split('\n').map((line, index) => (
                        <p key={index} className="mb-2">
                          {line}
                        </p>
                      ))}
                    </motion.div>
                  </div>
                </div>

                {/* 选择按钮 */}
                <AnimatePresence>
                  {showChoices && currentScene.choices.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-3"
                    >
                      {currentScene.choices.map((choice, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleChoice(index)}
                          disabled={selectedChoice !== null}
                          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                            selectedChoice === index
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          } ${selectedChoice !== null ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{choice.text}</span>
                            {selectedChoice === index && (
                              <ChevronRight className="w-5 h-5 text-blue-500" />
                            )}
                          </div>
                          {selectedChoice === index && (
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: '100%' }}
                              className="h-1 bg-blue-500 mt-2"
                            />
                          )}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* 进度指示器 */}
              <div className="flex justify-center space-x-2">
                {story?.scenes.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index < story.scenes.indexOf(currentScene) ||
                      (index === story.scenes.indexOf(currentScene) && currentScene.id === 'scene_end')
                        ? 'bg-blue-500'
                        : index === story.scenes.indexOf(currentScene)
                        ? 'bg-blue-300'
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MentorStoryPage;